import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { VehicleTelemetryDrawer } from '@/components/maintenance/VehicleTelemetryDrawer';
import { Search, ChevronRight, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/lib/api';

/**
 * Every vehicle, one to a row. Press one and it opens.
 *
 * The row carries what someone standing in a yard actually asks: whose truck is
 * it, which side is it on, is it running, when was it last inspected and when is
 * the next one due. Anything more belongs on the vehicle's own page, which is
 * one press away.
 *
 * Read from the inspection status view because it already joins the vehicle, its
 * side, its current driver and its inspection standing. A second query built to
 * say the same thing would eventually say it differently.
 */

interface Row {
  vehicle_id: string; plate_en: string | null; vehicle_code: string;
  vehicle_status: string; side: string | null; driver_name: string | null;
  last_periodic_at: string | null; next_periodic_due: string | null;
  due_reason: 'never' | 'after_change' | 'overdue' | 'ok' | 'inactive';
}

const DUE: Record<Row['due_reason'], { label: string; tone: string }> = {
  after_change: { label: 'Due — vehicle changed', tone: 'text-rose-600 dark:text-rose-400' },
  never:        { label: 'Never inspected',       tone: 'text-amber-600 dark:text-amber-400' },
  overdue:      { label: 'Overdue',               tone: 'text-amber-600 dark:text-amber-400' },
  ok:           { label: 'Up to date',            tone: 'text-emerald-600 dark:text-emerald-400' },
  inactive:     { label: 'Out of service',        tone: 'text-muted-foreground' },
};

const day = (s: string | null) => (s ? new Date(s).toISOString().slice(0, 10) : '—');

export function VehiclesPanel() {
  const [q, setQ] = useState('');
  // Opened beside the list. Leaving the page to read four numbers costs the
  // search and the scroll position, and both are the work.
  const [open, setOpen] = useState<{ id: string; plate: string | null } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['maintenance-vehicles'],
    queryFn: async () =>
      (await apiClient.get<{ items: Row[] }>('/api/v1/inspections/status')).data.items,
  });

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const all = data ?? [];
    if (!needle) return all;
    return all.filter((r) =>
      `${r.plate_en ?? ''} ${r.vehicle_code} ${r.driver_name ?? ''} ${r.side ?? ''}`
        .toLowerCase().includes(needle));
  }, [data, q]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-4 w-4 absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="ps-9"
            placeholder="Plate, code, driver or side…" />
        </div>
        <span className="text-sm text-muted-foreground">
          {isLoading ? 'loading…' : `${rows.length} of ${data?.length ?? 0} vehicles`}
        </span>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-muted-foreground">
            <tr>
              <th className="text-start px-3 py-2 font-medium whitespace-nowrap">Plate</th>
              <th className="text-start px-3 py-2 font-medium whitespace-nowrap">Code</th>
              <th className="text-start px-3 py-2 font-medium whitespace-nowrap">Driver</th>
              <th className="text-start px-3 py-2 font-medium whitespace-nowrap">Side</th>
              <th className="text-start px-3 py-2 font-medium whitespace-nowrap">Last inspection</th>
              <th className="text-start px-3 py-2 font-medium whitespace-nowrap">Next due</th>
              <th className="text-start px-3 py-2 font-medium whitespace-nowrap">Standing</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline" /> Loading…
              </td></tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">
                {q ? `Nothing matches “${q.trim()}”.` : 'No vehicles.'}
              </td></tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.vehicle_id}
                onClick={() => setOpen({ id: r.vehicle_id, plate: r.plate_en })}
                className="border-t border-border cursor-pointer hover:bg-muted/30 transition-colors"
              >
                <td className="px-3 py-2 font-mono whitespace-nowrap">{r.plate_en ?? '—'}</td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground whitespace-nowrap">{r.vehicle_code}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.driver_name ?? '—'}</td>
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.side ?? 'no side'}</td>
                <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{day(r.last_periodic_at)}</td>
                <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{day(r.next_periodic_due)}</td>
                <td className={`px-3 py-2 whitespace-nowrap ${DUE[r.due_reason].tone}`}>
                  {DUE[r.due_reason].label}
                </td>
                <td className="px-3 py-2 text-end text-muted-foreground">
                  <ChevronRight className="h-4 w-4 inline" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Press a row to open that vehicle — mileage, engine hours, position, today’s running and its fuel.
      </p>

      {open && (
        <VehicleTelemetryDrawer vehicleId={open.id} plate={open.plate} onClose={() => setOpen(null)} />
      )}
    </div>
  );
}
